/**
 * homeAnalytics.js — Analytics event tracking for Home suggestion system
 * 
 * Sends events to backend and maintains a local event log for admin debug panel.
 * Events in simulation mode are marked simulation_mode: true.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LOCAL_EVENTS = 50;
const LOCAL_KEY = 'hearen_home_analytics_log';

// ─── Local event log (for admin debug panel) ───
const getLocalLog = () => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
  } catch { return []; }
};

const appendLocalLog = (event) => {
  try {
    const log = getLocalLog();
    log.unshift(event); // newest first
    if (log.length > MAX_LOCAL_EVENTS) log.length = MAX_LOCAL_EVENTS;
    localStorage.setItem(LOCAL_KEY, JSON.stringify(log));
  } catch { /* noop */ }
};

/** Read all local events (for admin panel) */
export const getAnalyticsLog = () => getLocalLog();

/** Clear local event log */
export const clearAnalyticsLog = () => {
  try { localStorage.removeItem(LOCAL_KEY); } catch { /* noop */ }
};

// ─── Send event to backend (fire-and-forget) ───
const sendEvent = async (eventName, payload) => {
  const event = {
    event: eventName,
    timestamp: new Date().toISOString(),
    ...payload,
  };

  // Always store locally
  appendLocalLog(event);

  // Send to backend (non-blocking)
  try {
    const token = localStorage.getItem('hearen_token');
    await fetch(`${API_URL}/api/analytics/home-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(event),
    });
  } catch {
    // Silent fail — analytics should never break the app
  }
};

// ─── Build payload from suggestion state ───
const buildPayload = (suggestionState, simulationMode = false) => ({
  stage: suggestionState.stage || null,
  variant_id: suggestionState.variantId || null,
  variant_title: suggestionState.title || null,
  theme_context: suggestionState.debug?.lastThemeUsed || null,
  session_count: suggestionState.debug?.sessionCount || 0,
  days_since_install: suggestionState.debug?.daysSinceInstall || 0,
  time_of_day: suggestionState.debug?.timeOfDay || null,
  source: 'home',
  simulation_mode: simulationMode,
});

// ─── Public API ───
export const homeAnalytics = {
  impression: (state, sim = false) => sendEvent('home_suggestion_impression', buildPayload(state, sim)),
  click: (state, sim = false) => sendEvent('home_suggestion_click', buildPayload(state, sim)),
  dismiss: (state, sim = false) => sendEvent('home_suggestion_dismiss', buildPayload(state, sim)),
  personalizationStart: (state, sim = false) => sendEvent('personalization_start', buildPayload(state, sim)),
  personalizationComplete: (sim = false) => sendEvent('personalization_complete', {
    stage: 'COMPLETED',
    variant_id: 'completed',
    source: 'home',
    simulation_mode: sim,
  }),
};

export default homeAnalytics;
